import { first, pickBy } from 'lodash';

import { WEEK } from 'Components/SubscriptionOptionSelect/constants';

import {
  DEFAULT_AMOUNT,
  DEFAULT_CURRENCY,
  DEFAULT_DURATION,
  DEFAULT_PRICE,
  DEFAULT_SUBSCRIPTION_TERMS,
  DEFAULT_TAX_RATE,
  DISCOUNTED_OFFER_TYPES,
  OFFER_TYPE_REGULAR,
} from './constants';
import {
  AmountUnitType,
  PaymentMethodDetailsType,
  PaymentType,
  Product,
  ProductType,
  PurchaseType,
} from './products.types';
import { ProductsEntityState } from './types';

type SubscriptionBaseProductPriceDetails = {
  basePricePerTerm: number;
  baseProductTermAmount: number;
  subscriptionUpgradePricePerTerm: number;
  baseProductCurrency: string;
};

export function getSubscriptionBaseProductPriceDetails(
  subscriptionBaseProduct: Product | undefined
): SubscriptionBaseProductPriceDetails {
  if (!subscriptionBaseProduct) {
    return {
      basePricePerTerm: DEFAULT_PRICE,
      baseProductTermAmount: DEFAULT_SUBSCRIPTION_TERMS,
      subscriptionUpgradePricePerTerm: DEFAULT_PRICE,
      baseProductCurrency: DEFAULT_CURRENCY,
    };
  }

  const basePricePerTerm = subscriptionBaseProduct.pricePerTerm;
  const baseProductTermAmount = subscriptionBaseProduct.terms;
  const subscriptionUpgradePrice = subscriptionBaseProduct.adjustmentPrice ?? DEFAULT_PRICE;
  const subscriptionUpgradePricePerTerm = baseProductTermAmount
    ? subscriptionUpgradePrice / baseProductTermAmount
    : subscriptionUpgradePrice;

  return {
    basePricePerTerm,
    baseProductTermAmount,
    subscriptionUpgradePricePerTerm,
    baseProductCurrency: subscriptionBaseProduct.currency,
  };
}

/**
 * Maps products by ID to an array of products found in state.
 */
export const getProductsByIds = (productIds: string[], products: ProductsEntityState): Product[] =>
  productIds.map((id) => products[id]).filter(Boolean);

/**
 * @returns discounted product entities with the requested product type.
 */
export const getDiscountedProductsByType = (
  products: ProductsEntityState,
  productType: string | string[]
) =>
  pickBy(products, (product: Product): boolean => {
    const productTypes: string[] = [];
    return (
      productTypes.concat(productType).includes(product.productType) && isProductDiscounted(product)
    );
  });

/**
 * @returns non-discounted products with a particular product type.
 */
export const getNonDiscountedProductsByType = (
  products: ProductsEntityState,
  productType: string | string[] | undefined
): ProductsEntityState =>
  pickBy(products, (product: Product) => {
    if (productType === undefined) {
      return false;
    }

    const productTypes: string[] = [];
    return (
      productTypes.concat(productType).includes(product.productType) &&
      !isProductDiscounted(product)
    );
  });

/**
 * @returns all products with a particular product type.
 */
export const getAllProductsByType = (
  products: ProductsEntityState,
  productType: string | string[]
): ProductsEntityState =>
  pickBy(products, (product: Product) => {
    const productTypes: string[] = [];
    return productTypes.concat(productType).includes(product.productType);
  });

/**
 * @param productList - list of product entities
 * @returns the primary product from a list (eg. the initial selected product), if found.
 */
export const getPrimaryProductFromList = (productList: Product[]): Product | undefined =>
  first(productList.filter((product): boolean => product.isPrimary));

/**
 * @returns the base group product from a list (eg. the 1-month option), if found.
 *   If no product is marked as the base, it will use the first one.
 */
export const getBaseProductFromList = (productList: Product[]): Product | undefined =>
  first(productList.filter((product) => product.isBaseGroup));

/**
 * @returns if product is discounted.
 */
export const isProductDiscounted = (product: Product): boolean => {
  if (product.offerType === OFFER_TYPE_REGULAR) {
    return false;
  }
  return DISCOUNTED_OFFER_TYPES.includes(product.offerType) || !!product.originalProductId;
};

/**
 * @returns if product is weekly.
 */
export const isWeeklyProduct = (product: Product) => product.termsUnit === WEEK;

/**
 * Removes replacement products from product list
 */
export const removeReplacementProducts = (products: ProductsEntityState): ProductsEntityState =>
  pickBy(products, (product: Product) => !getProductIsReplacement(product));

/**
 * Removes replaceable products from product list
 */
export const removeReplaceableProducts = (products: ProductsEntityState): ProductsEntityState =>
  pickBy(products, (product: Product) => !getProductIsReplaceable(product));

/**
 * @returns weekly products with a particular product type.
 */
export const getWeeklyProductsByType = (
  products: ProductsEntityState,
  productType: string | string[] | undefined
): ProductsEntityState =>
  pickBy(products, (product: Product): boolean => {
    if (productType === undefined) {
      return false;
    }

    const productTypes: string[] = [];
    return (
      productTypes.concat(productType).includes(product.productType) &&
      isWeeklyProduct(product) &&
      !getProductIsReplacement(product)
    );
  });

/**
 * @returns amount from product, defaulted to {@link DEFAULT_AMOUNT}.
 */
export const getProductAmount = (product?: Product): number => product?.amount ?? DEFAULT_AMOUNT;

/**
 * @returns adjustment price from product (for upgrades), defaulted to {@link DEFAULT_PRICE}.
 */
export const getProductAdjustmentPrice = (product: Product): number =>
  product.adjustmentPrice ?? DEFAULT_PRICE;

/**
 * @returns productType from product.
 */
export const getProductTypeGuaranteed = (product: Product): ProductType => product.productType;

/**
 * TODO: GUARANTEE-DATA -- Attempt to migrate usages to {@link getProductType} in hopes to remove this helper.
 *
 * @returns productType from product if defined, otherwise undefined.
 */
export const getProductType = (product?: Product): ProductType | undefined => product?.productType;
/**
 * @returns paymentMethods from product.
 */

export const getProductPaymentMethods = (product?: Product): PaymentMethodDetailsType[] =>
  product?.paymentMethods ?? [];

/**
 * TODO: GUARANTEE-DATA -- Verify whether the product should be optional after migrating all relevant JS files to TS.
 *
 * @returns purchaseType from product.
 */
export const getProductPurchaseType = (product?: Product): PurchaseType | undefined =>
  product?.purchaseType;

/**
 * TODO: GUARANTEE-DATA -- Attempt to migrate all usages to {@link getProductIdGuaranteed} and consolidate these two helpers into one.
 *
 * @returns productId from provided product; undefined if provided undefined product.
 */
export const getProductId = (product?: Product): string | undefined => product?.productId;

/**
 * @returns productId from product.
 */
export const getProductIdGuaranteed = (product: Product): string => product.productId;

/**
 * @returns duration of product, defaulted to {@link DEFAULT_DURATION}.
 */
export const getProductDuration = (product: Product): number =>
  product.duration ?? DEFAULT_DURATION;

/**
 * @returns icon of product.
 */
export const getProductIcon = (product: Product): string | undefined => product.iconUrl;

/**
 * @returns if product is best value.
 */
export const getProductIsBestValue = (product: Product): boolean => product.isBestValue;

/**
 * @returns if product is most popular.
 */
export const getProductIsMostPopular = (product: Product): boolean => product.isMostPopular;

/**
 * @returns if product is replacement, based on REPLACEMENT tag.
 */
export const getProductIsReplacement = (product: Product) => product.isReplacement;

/**
 * @returns if product is replaceable, based on presence of REPLACEABLE tag.
 */
export const getProductIsReplaceable = (product: Product): boolean => product.isReplaceable;

/**
 * TODO: GUARANTEE-DATA -- Verify whether the product should be optional after migrating all relevant JS files to TS.
 *
 * @returns terms of product.
 */
export const getProductTerms = (product?: Product) => product?.terms;

/**
 * TODO: GUARANTEE-DATA -- Verify whether the product should be optional after migrating all relevant JS files to TS.
 *
 * @return terms unit of product
 */
export const getProductTermsUnit = (product?: Product): AmountUnitType | undefined =>
  product?.termsUnit;

/**
 * @returns signature of product
 */
export const getProductSignature = (product: Product): string | undefined =>
  product.productSignature;

/**
 * TODO: GUARANTEE-DATA -- Verify whether the product should be optional after migrating all relevant JS files to TS.
 *
 * @returns discount percentage of product.
 */
export const getProductDiscountPercentage = (product: Product): number | undefined =>
  product.discountPercentage;

/**
 * @returns original price of product
 */
export const getProductOriginalPrice = (product: Product): number | undefined =>
  product.originalPrice;

/**
 * @returns original id from intro pricing or sub offer product.
 */
export const getProductOriginalId = (product?: Product): string | undefined =>
  product?.originalProductId;

/**
 * Finds a particular paymentMethod based on the payment type (default: credit_card)
 */
export function findPaymentMethodDetailsByType(
  product: Product | undefined,
  paymentType: PaymentType | undefined
): PaymentMethodDetailsType | undefined {
  return product?.paymentMethods?.find((method) => method.platform === paymentType);
}

/**
 * Finds product field by the payment type (default: field associated with the payment method that has the highest price)
 *
 * @returns [field] of Product or PaymentMethodDetailsType
 */
export const getProductFieldByPaymentType = <
  TKey extends keyof (Product | PaymentMethodDetailsType)
>(
  fieldName: TKey,
  product: Product | undefined,
  paymentType?: PaymentType
): Product[TKey] | PaymentMethodDetailsType[TKey] | undefined =>
  paymentType
    ? findPaymentMethodDetailsByType(product, paymentType)?.[fieldName]
    : product?.[fieldName];

/**
 * Finds product field by the payment type (default: field associated with the payment method that has the highest price).
 *
 * @returns [field] of Product or PaymentMethodDetailsType
 */
export function findProductPaymentTypeField<TKey extends keyof PaymentMethodDetailsType>(
  fieldName: TKey,
  product: Product | undefined,
  paymentType: PaymentType | undefined
): PaymentMethodDetailsType[TKey] | undefined {
  return findPaymentMethodDetailsByType(product, paymentType)?.[fieldName];
}

/**
 * Fields below exist inside both the product and product.paymentMethods, specify payment type (platform) to extract fields under each payment method
 * Note that for fields outside of paymentMethods are considered as default (associated with the payment method with highest price)
 *
 * Ex.
 * plus_subscription_999_1m_1pd: {
 *   amount: 1,
 *   iconUrl: 'https://tinderphotos-d1-ue1.s3.amazonaws.com/product_images/plus/plus.png',
 *   offerType: 'REGULAR',
 *   paymentMethods: [
 *     {
 *       platform: 'credit_card',
 *       skuId: 'plus_subscription_999_1m_1pd',
 *       discount: 0,
 *       requireZip: true,
 *       price: 8.99,
 *       isVat: false,
 *       taxRate: 0,
 *       currency: 'USD',
 *       pricePerTerm: 8.99
 *     },
 *     ...
 *   ],
 *   ...
 *   // Fields below are considered as default (associated with the payment method with highest price)
 *   platform: 'credit_card',
 *   skuId: 'plus_subscription_999_1m_1pd',
 *   discount: 0,
 *   requireZip: true,
 *   price: 8.99,
 *   isVat: false,
 *   taxRate: 0,
 *   currency: 'USD',
 *   pricePerTerm: 8.99,
 * },
 */

/**
 * @return currency of product, optionally by the payment type, defaulted to {@link DEFAULT_CURRENCY}.
 */
export const getProductCurrency = (product: Product, paymentType?: PaymentType): string =>
  getProductFieldByPaymentType('currency', product, paymentType) ?? DEFAULT_CURRENCY;

/**
 * @returns product discount by the payment type.
 */
export const getProductDiscount = (product: Product): number => product.discount;

/**
 * @returns product offerId by the payment type.
 */
export const getProductOfferId = (
  product: Product,
  paymentType?: PaymentType
): string | undefined => getProductFieldByPaymentType('offerId', product, paymentType);

/**
 * @returns price of product by the payment type, defaulted to {@link DEFAULT_PRICE}.
 */
export const getProductPrice = (product: Product, paymentType?: PaymentType): number =>
  getProductFieldByPaymentType('price', product, paymentType) ?? DEFAULT_PRICE;

/**
 * @returns pricePerTerm of product by the payment type, defaulted to {@link DEFAULT_PRICE}.
 */
export const getProductPricePerTerm = (
  product: Product,
  paymentType?: PaymentType
): number | undefined =>
  getProductFieldByPaymentType('pricePerTerm', product, paymentType) ?? DEFAULT_PRICE;

/**
 * @returns product skuId by the payment type
 */
export function getProductSkuId(product: Product, paymentType: PaymentType): string | undefined {
  return findProductPaymentTypeField('skuId', product, paymentType);
}

/**
 * @return taxRate of product, defaulted to {@link DEFAULT_TAX_RATE}.
 */
export const getProductTaxRate = (product: Product, paymentType?: PaymentType): number =>
  getProductFieldByPaymentType('taxRate', product, paymentType) ?? DEFAULT_TAX_RATE;
